import React, { useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const ItemType = 'ITEM';

const DraggableItem = ({ item, index, moveItem, moveCrossContainer, containerName }) => {
    const ref = useRef(null);

    const [, drop] = useDrop({
        accept: ItemType,
        hover(draggedItem, monitor) {
            const dragIndex = draggedItem.index;
            const hoverIndex = index;

            if (!ref.current) return;

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (draggedItem.container === containerName) {
                if (dragIndex === hoverIndex) return;
                if ((dragIndex < hoverIndex && hoverClientY < hoverMiddleY) || (dragIndex > hoverIndex && hoverClientY > hoverMiddleY)) return;

                moveItem(dragIndex, hoverIndex, containerName);
                draggedItem.index = hoverIndex;
            } else {
                moveCrossContainer(draggedItem, hoverIndex, containerName);
                draggedItem.index = hoverIndex;
                draggedItem.container = containerName;
            }
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: ItemType,
        item: { ...item, index, container: containerName },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    drag(drop(ref));

    return (
        <div ref={ref} className="p-2 my-1 bg-light cursor-move" style={{ opacity: isDragging ? 0.5 : 1, border: "1px solid #cecece" }}>
            {item.label}
        </div>
    );
};

const DropContainer = ({ title, items, setItems, otherItems, setOtherItems, containerName, }) => {
    const ref = useRef(null);

    const moveItem = (fromIndex, toIndex) => {
        const updated = [...items];
        const [moved] = updated.splice(fromIndex, 1);
        updated.splice(toIndex, 0, moved);
        setItems(updated);
    };

    const moveCrossContainer = (item, toIndex, toContainer) => {
        if (toContainer !== containerName) return;

        setOtherItems((prev) => prev.filter((i) => i.id !== item.id));

        setItems((prev) => {
            const newItems = [...prev];
            if (!newItems.find((i) => i.id === item.id)) {
                newItems.splice(toIndex, 0, { id: item.id, label: item.label });
            }
            return newItems;
        });
    };

    const [, drop] = useDrop({
        accept: ItemType,
        drop: (item, monitor) => {
            if (!monitor.didDrop()) {
                moveCrossContainer(item, items.length, containerName);
            }
        },
    });

    drop(ref);

    return (
        <div ref={ref} className="flex-fill p-3 h-500px overflow-auto" style={{border: "1px solid black"}}>
            <h4>{title}</h4>
            {items.map((item, index) => (
                <DraggableItem
                    key={item.id}
                    item={item}
                    index={index}
                    moveItem={moveItem}
                    moveCrossContainer={moveCrossContainer}
                    containerName={containerName}
                />
            ))}
        </div>
    );
};

const MultipleContainers = (props) => {
    const { leftItems, setLeftItems, rightItems, setRightItems } = props;

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="d-flex gap-3 p-3">
                <DropContainer
                    title="Columns not in CSV"
                    items={leftItems}
                    setItems={setLeftItems}
                    otherItems={rightItems}
                    setOtherItems={setRightItems}
                    containerName="left"
                />
                <DropContainer
                    title="Columns in CSV"
                    items={rightItems}
                    setItems={setRightItems}
                    otherItems={leftItems}
                    setOtherItems={setLeftItems}
                    containerName="right"
                />
            </div>
        </DndProvider>
    );
};

export default MultipleContainers;